import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  ChartColumn,
  ChartLine,
  Headset,
  House,
  LayoutDashboard,
  LogOut,
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  Plug,
  ShieldCog,
  SlidersHorizontal,
  Ticket,
  UserPlus,
  Wrench,
  WavesHorizontal,
} from "lucide-react";
import { useDispatch } from "react-redux";
import { logout } from "../../features/userSlice";
import { useContextStateManagement } from "../../context/ContextApi";
import { Link, useNavigate } from "react-router-dom";
import { setIsBulkCallsEnabled } from "../../features/bulkCallsSlice";
import logoImage from "../../assets/images/Logo.png";
import "./Sidebar.css";

export const Sidebar = ({
  collapsed,
  onToggleCollapse,
  outBoundLeadsDetail,
  setOutBoundLeadsDetail,
  setIsUploadLead,
  setIsTabClick,
  setUserDataAccess,
  setCurrentRoute,
}) => {
  const {
    timeoutIdRef,
    sideBarLinkActive,
    activeTab,
    hasPageAccess,
    clearSelectedOrg,
    setSideBarLinkActive,
    fetchApiHandle,
    setActiveTab,
    setUserType,
    PracticeId,
    userType,
    rightsEnabled,
    credentialsSaved,
    closePhoneTab,
    logoutFromServer,
  } = useContextStateManagement();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const tokenExpiryLogoutHandle = () => {
    logoutFromServer();
    setActiveTab("inBoundCalls");
    setIsTabClick(true);
    setSideBarLinkActive("careConsole");
    clearSelectedOrg();
    setUserDataAccess([]);
    setUserType("");
    setCurrentRoute("login");
    dispatch(logout());
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
      dispatch(setIsBulkCallsEnabled(false));
    }
  };

  const sideBarLinkHandle = (nextLink) => {
    if (nextLink === "careConsole") {
      setSideBarLinkActive("careConsole");
    } else if (nextLink === "bulkUpload") {
      setSideBarLinkActive("bulkUpload");
    } else if (nextLink === "callSettings") {
      setSideBarLinkActive("callSettings");
    } else if (nextLink === "onBoardingForm") {
      setSideBarLinkActive("onBoardingForm");
    } else if (nextLink === "dashboard") {
      setSideBarLinkActive("dashboard");
    } else if (nextLink === "homepage") {
      setSideBarLinkActive("homepage");
    } else if (nextLink === "adminDashboard") {
      setSideBarLinkActive("adminDashboard");
    } else if (nextLink === "voiceOps") {
      setSideBarLinkActive("voiceOps");
    } else if (nextLink === "addUsers") {
      setSideBarLinkActive("addUsers");
    } else if (nextLink === "manageOrg") {
      setSideBarLinkActive("manageOrg");
    } else if (nextLink === "dialer") {
      setSideBarLinkActive("dialer");
    } else if (nextLink === "support") {
      setSideBarLinkActive("support");
    } else if (nextLink === "tickets") {
      setSideBarLinkActive("tickets");
    }
  };

  const callSettingsHandle = async () => {
    sideBarLinkHandle("callSettings");
    closePhoneTab();
    navigate("/call-settings");
  };

  useEffect(() => {
    if (PracticeId && sideBarLinkActive === "callSettings") {
      fetchApiHandle();
    }
  }, [PracticeId, sideBarLinkActive, fetchApiHandle]);

  const canViewTickets = hasPageAccess("tickets", false);

  const renderLabel = (label) => (
    <span
      className={`sidebar-label${collapsed ? " collapsed" : ""}`}
      aria-hidden={collapsed}
    >
      {label}
    </span>
  );

  const SidebarIcon = ({ icon: Icon }) => (
    <Icon size={18} strokeWidth={1.85} aria-hidden="true" />
  );

  // Collapsed-sidebar hover tooltip: rendered via a portal straight onto document.body,
  // positioned from the hovered item's own bounding box, instead of a pure-CSS ::after popout -
  // the sidebar and its scrollable sections both clip overflow (needed for the collapse
  // animation and internal scrolling), which was silently clipping a same-element popout the
  // same way it did for the info-tooltips elsewhere in the app before those moved to a portal.
  const [sidebarTooltip, setSidebarTooltip] = useState(null);

  useEffect(() => {
    if (!collapsed) setSidebarTooltip(null);
  }, [collapsed]);

  const showSidebarTooltip = (label) => (event) => {
    if (!collapsed) return;
    const rect = event.currentTarget.getBoundingClientRect();
    setSidebarTooltip({
      label,
      top: rect.top + rect.height / 2,
      left: rect.right + 10,
    });
  };
  const hideSidebarTooltip = () => setSidebarTooltip(null);

  const tooltipProps = (label) => ({
    "aria-label": label,
    onMouseEnter: showSidebarTooltip(label),
    onMouseLeave: hideSidebarTooltip,
    onFocus: showSidebarTooltip(label),
    onBlur: hideSidebarTooltip,
  });

  const primaryTabPaths = {
    inBoundCalls: "/inbound",
    leadGTab: "/outbound",
    summaryTab: "/summary",
  };

  const activatePrimaryTab = (tabId) => {
    closePhoneTab();
    setSideBarLinkActive("careConsole");
    setActiveTab(tabId);
    navigate(primaryTabPaths[tabId] || "/");
  };

  return (
    <>
      <div
        className={`sideBar sidebar fs-6 text-white min-vh-100${
          collapsed ? " collapsed" : ""
        }`}
      >
        <div
          className={`brand${collapsed ? " collapsed" : ""}`}
          onClick={() => onToggleCollapse?.()}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onToggleCollapse?.();
            }
          }}
        >
          <img src={logoImage} alt="Allyzent Logo" className="sidebar-logo" />
          <h2 className="sidebar-title">ALLYZENT AI</h2>
        </div>
        <div className="sidebar-sections">
          <div className="sidebar-section">
            <div className="sidebar-section-header">Console</div>
            <ul className="nav menu flex-column mb-0" id="menu-console">
              <li className="nav-item">
                <Link
                  to="/homepage"
                  className={`nav-link menu-item align-middle px-0 ${
                    sideBarLinkActive === "homepage" ? "active" : ""
                  }`}
                  onClick={() => sideBarLinkHandle("homepage")}
                  {...tooltipProps("Homepage")}
                >
                  <SidebarIcon icon={House} />
                  {renderLabel("Homepage")}
                </Link>
              </li>
              {hasPageAccess("careConsole", true) && (
                <li className="nav-item">
                  <Link
                    to="/inbound"
                    className={`nav-link menu-item align-middle px-0 ${
                      sideBarLinkActive === "careConsole" && activeTab === "inBoundCalls"
                        ? "active"
                        : ""
                    }`}
                    onClick={() => activatePrimaryTab("inBoundCalls")}
                    {...tooltipProps("Inbound")}
                  >
                    <SidebarIcon icon={PhoneIncoming} />
                    {renderLabel("Inbound")}
                  </Link>
                </li>
              )}
              {hasPageAccess("careConsole", true) && (
                <li className="nav-item">
                  <Link
                    to="/outbound"
                    className={`nav-link menu-item align-middle px-0 ${
                      sideBarLinkActive === "careConsole" && activeTab === "leadGTab"
                        ? "active"
                        : ""
                    }`}
                    onClick={() => activatePrimaryTab("leadGTab")}
                    {...tooltipProps("Outbound")}
                  >
                    <SidebarIcon icon={PhoneOutgoing} />
                    {renderLabel("Outbound")}
                  </Link>
                </li>
              )}
              {hasPageAccess("careConsole", true) && (
                <li className="nav-item">
                  <Link
                    to="/summary"
                    className={`nav-link menu-item align-middle px-0 ${
                      sideBarLinkActive === "careConsole" && activeTab === "summaryTab"
                        ? "active"
                        : ""
                    }`}
                    onClick={() => activatePrimaryTab("summaryTab")}
                    {...tooltipProps("Summary")}
                  >
                    <SidebarIcon icon={ChartLine} />
                    {renderLabel("Summary")}
                  </Link>
                </li>
              )}
              {hasPageAccess("callSettings", true) && (
                <li className="nav-item">
                  <Link
                    to="/call-settings"
                    className={`nav-link align-middle ${
                      sideBarLinkActive === "callSettings" ? "active" : ""
                    } menu-item px-0`}
                    onClick={callSettingsHandle}
                    {...tooltipProps("Call Settings")}
                  >
                    <SidebarIcon icon={SlidersHorizontal} />
                    {renderLabel("Call Settings")}
                  </Link>
                </li>
              )}
              {hasPageAccess("dialer", true) && (
                <li className="nav-item">
                  <Link
                    to="/dialer"
                    className={`nav-link align-middle ${
                      sideBarLinkActive === "dialer" ? "active" : ""
                    } menu-item px-0`}
                    onClick={() => sideBarLinkHandle("dialer")}
                    {...tooltipProps("Dialer")}
                  >
                    <SidebarIcon icon={Phone} />
                    {renderLabel("Dialer")}
                  </Link>
                </li>
              )}
            </ul>
          </div>

          <div className="sidebar-section">
            <div className="sidebar-section-header">Insights</div>
            <ul className="nav menu flex-column mb-0" id="menu-insights">
              {hasPageAccess("dashboard", true) &&
                !(
                  activeTab === "uatTab" ||
                  activeTab === "inboundLogsTab" ||
                  activeTab === "chatG"
                ) && (
                <li className="nav-item">
                  <Link
                    to="/Dashboard"
                    className={`nav-link ${
                      sideBarLinkActive === "dashboard" ? "active" : ""
                    } menu-item align-middle px-0`}
                    onClick={() => setSideBarLinkActive("dashboard")}
                    {...tooltipProps("Dashboards")}
                  >
                    <SidebarIcon icon={LayoutDashboard} />
                    {renderLabel("Dashboards")}
                  </Link>
                </li>
              )}
              {userType === "admin" && (
                <li className="nav-item">
                  <Link
                    to="/AdminDashboard"
                    className={`nav-link ${
                      sideBarLinkActive === "adminDashboard" ? "active" : ""
                    } menu-item align-middle px-0`}
                    onClick={() => sideBarLinkHandle("adminDashboard")}
                    {...tooltipProps("Admin Insights")}
                  >
                    <SidebarIcon icon={ChartColumn} />
                    {renderLabel("Admin Insights")}
                  </Link>
                </li>
              )}
              {hasPageAccess("voiceOps", false) && (
                <li className="nav-item">
                  <Link
                    to="/VoiceOps"
                    className={`nav-link ${
                      sideBarLinkActive === "voiceOps" ? "active" : ""
                    } menu-item align-middle px-0`}
                    onClick={() => sideBarLinkHandle("voiceOps")}
                    {...tooltipProps("Voice Ops")}
                  >
                    <SidebarIcon icon={WavesHorizontal} />
                    {renderLabel("Voice Ops")}
                  </Link>
                </li>
              )}
            </ul>
          </div>

          <div className="sidebar-section">
            <div className="sidebar-section-header">Workflow</div>
            <ul className="nav menu flex-column mb-0" id="menu-workflow">
              {hasPageAccess("agentConfig", false) && (
                <li className="nav-item">
                  <Link
                    to="/agent-config"
                    id="agentConfig"
                    className={`nav-link align-middle ${
                      sideBarLinkActive === "agentConfig" ? "active" : ""
                    } menu-item px-0`}
                    onClick={() => {
                      closePhoneTab();
                      setSideBarLinkActive("agentConfig");
                      navigate("/agent-config");
                    }}
                    {...tooltipProps("Agent Config")}
                  >
                    <SidebarIcon icon={SlidersHorizontal} />
                    {renderLabel("Agent Config")}
                  </Link>
                </li>
              )}
              {hasPageAccess(
                "apiIntegration",
                (userType === "admin" ||
                  (userType === "client" && rightsEnabled === true)) &&
                  (activeTab == "inBoundCalls" ||
                    activeTab == "leadGTab" ||
                    activeTab == "summaryTab"),
              ) && (
                  <li className="nav-item">
                    <Link
                      to="/api-integration"
                      id="apiIntegration"
                      className={`nav-link align-middle ${
                        sideBarLinkActive === "apiIntegration" ? "active" : ""
                      } menu-item px-0`}
                      onClick={() => {
                        closePhoneTab();
                        setSideBarLinkActive("apiIntegration");
                        navigate("/api-integration");
                      }}
                      {...tooltipProps("API Integration")}
                    >
                      <SidebarIcon icon={Plug} />
                      {renderLabel("API Integration")}
                    </Link>
                  </li>
                )}
              {hasPageAccess("toolDefinitions", false) && (
                <li className="nav-item">
                  <Link
                    to="/tools"
                    id="toolDefinitions"
                    className={`nav-link align-middle ${
                      sideBarLinkActive === "toolDefinitions" ? "active" : ""
                    } menu-item px-0`}
                    onClick={() => {
                      closePhoneTab();
                      setSideBarLinkActive("toolDefinitions");
                      navigate("/tools");
                    }}
                    {...tooltipProps("Tools")}
                  >
                    <SidebarIcon icon={Wrench} />
                    {renderLabel("Tools")}
                  </Link>
                </li>
              )}
              {userType === "admin" && (
                <li className="nav-item">
                  <Link
                    to="/add-users"
                    className={`nav-link ${
                      sideBarLinkActive === "addUsers" ? "active" : ""
                    } menu-item align-middle px-0`}
                    onClick={() => {
                      closePhoneTab();
                      sideBarLinkHandle("addUsers");
                      navigate("/add-users");
                    }}
                    {...tooltipProps("Add Users")}
                  >
                    <SidebarIcon icon={UserPlus} />
                    {renderLabel("Add Users")}
                  </Link>
                </li>
              )}
              {userType === "admin" && (
                <li className="nav-item">
                  <Link
                    to="/admin-center"
                    className={`nav-link ${
                      sideBarLinkActive === "manageOrg" ? "active" : ""
                    } menu-item align-middle px-0`}
                    onClick={() => {
                      closePhoneTab();
                      sideBarLinkHandle("manageOrg");
                      navigate("/admin-center");
                    }}
                    {...tooltipProps("Admin Center")}
                  >
                    <SidebarIcon icon={ShieldCog} />
                    {renderLabel("Admin Center")}
                  </Link>
                </li>
              )}
            </ul>
          </div>

          <div className="sidebar-section">
            <div className="sidebar-section-header">Help</div>
            <ul className="nav menu flex-column mb-0" id="menu-help">
              {hasPageAccess("support", true) && (
                <li className="nav-item">
                  <Link
                    to="/support"
                    className={`nav-link align-middle ${
                      sideBarLinkActive === "support" ? "active" : ""
                    } menu-item px-0`}
                    onClick={() => {
                      closePhoneTab();
                      sideBarLinkHandle("support");
                    }}
                    {...tooltipProps("Support")}
                  >
                    <SidebarIcon icon={Headset} />
                    {renderLabel("Support")}
                  </Link>
                </li>
              )}
              {canViewTickets ? (
                <li className="nav-item">
                  <Link
                    to="/tickets"
                    className={`nav-link align-middle ${
                      sideBarLinkActive === "tickets" ? "active" : ""
                    } menu-item px-0`}
                    onClick={() => {
                      closePhoneTab();
                      sideBarLinkHandle("tickets");
                    }}
                    {...tooltipProps("Tickets")}
                  >
                    <SidebarIcon icon={Ticket} />
                    {renderLabel("Tickets")}
                  </Link>
                </li>
              ) : null}
            </ul>
          </div>
        </div>
        <div className="sidebar-footer">
          <div className="sidebar-divider" />
          <Link
            className="sidebar-signout-btn nav-link menu-item align-middle px-0"
            onClick={tokenExpiryLogoutHandle}
            {...tooltipProps("Sign out")}
          >
            <SidebarIcon icon={LogOut} />
            {renderLabel("Sign out")}
          </Link>
        </div>
      </div>
      {sidebarTooltip
        ? createPortal(
            <span
              className="sidebar-floating-tooltip"
              role="tooltip"
              style={{ top: sidebarTooltip.top, left: sidebarTooltip.left }}
            >
              {sidebarTooltip.label}
            </span>,
            document.body,
          )
        : null}
    </>
  );
};
